import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Text, View } from "react-native";
import { Svg } from "react-native-svg";

const SCOPE = "BADGE";

const badgeStyle = tva({
	base: "flex-row items-center rounded-full data-[disabled=true]:opacity-50 px-2 py-1",
	variants: {
		action: {
			error: "",
			warning: "",
			success: "bg-success-100 border-success-300",
			info: "bg-tertiary-200 border-tertiary-700",
			muted: "bg-secondary-0 border-secondary-100",
		},
		variant: {
			solid: "",
			outline: "border",
		},
		size: {
			sm: "",
			md: "",
			lg: "",
		},
	},
	compoundVariants: [
		{
			action: "error",
			variant: "solid",
			className: "bg-error-600",
		},
		{
			action: "error",
			variant: "outline",
			className: "bg-error-0 border-error-300",
		},
		{
			action: "warning",
			variant: "solid",
			className: "bg-warning-600",
		},
		{
			action: "warning",
			variant: "outline",
			className: "bg-warning-50 border-warning-600",
		},
		{
			action: "success",
			variant: "solid",
			className: "bg-success-700",
		},
		{
			action: "success",
			variant: "outline",
			className: "bg-success-100 border-success-300",
		},
		{
			action: "info",
			variant: "solid",
			className: "bg-tertiary-900",
		},
		{
			action: "info",
			variant: "outline",
			className: "bg-tertiary-200 border-tertiary-700",
		},
		{
			action: "muted",
			variant: "solid",
			className: "bg-secondary-500",
		},
		{
			action: "muted",
			variant: "outline",
			className: "bg-secondary-0 border-secondary-100",
		},
	],
});

const badgeTextStyle = tva({
	base: "font-medium tracking-normal",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		size: {
			sm: "text-xs",
			md: "text-sm",
			lg: "text-base",
		},
		action: {
			error: "text-error-600",
			warning: "text-warning-700",
			success: "text-success-700",
			info: "text-tertiary-950",
			muted: "text-secondary-400",
		},
	},
	compoundVariants: [
		{
			variant: "solid",
			action: ["error", "warning", "success", "info", "muted"],
			class: "text-neutral-0",
		},
	],
});

const badgeIconStyle = tva({
	base: "fill-none",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "text-error-600",
			warning: "text-warning-700",
			success: "text-success-700",
			info: "text-tertiary-950",
			muted: "text-secondary-400",
		},
		size: {
			sm: "h-3.5 w-3.5",
			md: "h-4 w-4",
			lg: "h-4.5 w-4.5",
		},
	},
	compoundVariants: [
		{
			variant: "solid",
			action: ["error", "warning", "success", "info", "muted"],
			class: "text-neutral-0",
		},
	],
});

const ContextView = withStyleContext(View, SCOPE);

cssInterop(PrimitiveIcon, {
	className: {
		target: "style",
		nativeStyleToProp: {
			height: true,
			width: true,
			fill: true,
			color: "classNameColor",
			stroke: true,
		} as any,
	},
});

export type IBadgeProps = React.ComponentPropsWithoutRef<typeof ContextView> & VariantProps<typeof badgeStyle>;
function Badge({
	children,
	action = "muted",
	variant = "solid",
	size = "md",
	className,
	...props
}: { className?: string } & IBadgeProps) {
	return (
		<ContextView
			className={badgeStyle({ action, variant, class: className })}
			{...props}
			context={{
				action,
				variant,
				size,
			}}
		>
			{children}
		</ContextView>
	);
}

type IBadgeTextProps = React.ComponentPropsWithoutRef<typeof Text> & VariantProps<typeof badgeTextStyle>;

const BadgeText = React.forwardRef<React.ComponentRef<typeof Text>, IBadgeTextProps>(function BadgeText(
	{ children, className, size, ...props },
	ref,
) {
	const { size: parentSize, action: parentAction, variant: parentVariant } = useStyleContext(SCOPE);
	return (
		<Text
			ref={ref}
			className={badgeTextStyle({
				parentVariants: {
					variant: parentVariant,
					action: parentAction,
					size: parentSize,
				},
				size,
				class: className,
			})}
			{...props}
		>
			{children}
		</Text>
	);
});

type IBadgeIconProps = React.ComponentPropsWithoutRef<typeof PrimitiveIcon> & VariantProps<typeof badgeIconStyle>;

const BadgeIcon = React.forwardRef<React.ComponentRef<typeof Svg>, IBadgeIconProps>(function BadgeIcon(
	{ className, size, ...props },
	ref,
) {
	const { size: parentSize, action: parentAction, variant: parentVariant } = useStyleContext(SCOPE);

	if (typeof size === "number") {
		return <UIIcon ref={ref} {...props} className={badgeIconStyle({ class: className })} size={size} />;
	}

	if ((props?.height !== undefined || props?.width !== undefined) && size === undefined) {
		return <UIIcon ref={ref} {...props} className={badgeIconStyle({ class: className })} />;
	}

	return (
		<UIIcon
			className={badgeIconStyle({
				parentVariants: {
					size: parentSize,
					action: parentAction,
					variant: parentVariant,
				},
				size,
				class: className,
			})}
			{...props}
			ref={ref}
		/>
	);
});

Badge.displayName = "Badge";
BadgeText.displayName = "BadgeText";
BadgeIcon.displayName = "BadgeIcon";

export { Badge, BadgeIcon, BadgeText };
